import { FnLoader } from '../../components/elements/FnLoader/index';
import FnGroupMultiSelect from '../../components/blocks/Select2/FnGroupMultiselect';
import FnTextareaField from '../../components/elements/FnTextareaField';
import { Field, Form, Formik } from 'formik';
import React, { FC, memo, useEffect, useState } from 'react';
import { CONFIGURATION_PLACEHOLDERS, CONFIGURATION_TITLES, ConfigurationFields, VALIDATION_SCHEMA } from './constants';
import { DivFormWrapper, DivRow } from './styles';
import { TAssignTask, TSaveFormData } from './types';
import { getInitialConfiguration, getInitialOptionsConfiguration, getValuesForSave } from './utils';
import FnAntModal from '../../components/blocks/FnAntModal';

type TProps = {
  data: TAssignTask;
  count: number;
  isLoading: boolean;
  onSave: (data: TSaveFormData) => void;
  onClose: () => void;
};

const AssignTasksModal: FC<TProps> = ({ data, count, isLoading, onSave, onClose }) => {
  const [initialValues, setInitialValues] = useState(null);
  const [options, setOptions] = useState(null);

  useEffect(() => {
    if (data) {
      const cloneData = JSON.parse(JSON.stringify(data));
      setInitialValues(getInitialConfiguration());
      setOptions(getInitialOptionsConfiguration(cloneData));
    }
  }, [data]);

  const handleClose = () => onClose();

  const handleSubmitForm = (values) => {
    const { defaultTasks, defaultTeamMembers } = getValuesForSave(values, data);

    onSave({
      ...values,
      defaultTasks,
      defaultTeamMembers
    });
  };

  return (
    <>
      {isLoading && <FnLoader fixed={true} />}
      {initialValues !== null && options !== null && (
        <Formik
          initialValues={initialValues}
          validationSchema={VALIDATION_SCHEMA}
          validateOnMount={true}
          onSubmit={(fields) => handleSubmitForm(fields)}
        >
          {({ submitForm, isValid }) => (
            <Form>
              <FnAntModal
                isOpen={true}
                titleText="Assign tasks"
                titleCount={`${count} selected item`}
                onClose={handleClose}
                okDisabled={!isValid}
                okText="Assign"
                onOk={submitForm}
                cancelText="Cancel"
                onCancel={handleClose}
              >
                <DivFormWrapper>
                  <DivRow>
                    <Field name={ConfigurationFields.DefaultTasks}>
                      {({ field, form }) => (
                        <FnGroupMultiSelect
                          title={CONFIGURATION_TITLES[ConfigurationFields.DefaultTasks]}
                          isRequired={true}
                          options={options[ConfigurationFields.Tasks]}
                          value={field.value}
                          placeholder={CONFIGURATION_PLACEHOLDERS[ConfigurationFields.DefaultTasks]}
                          onChange={(selected) => form.setFieldValue(ConfigurationFields.DefaultTasks, selected)}
                        />
                      )}
                    </Field>
                  </DivRow>
                  <DivRow>
                    <Field name={ConfigurationFields.DefaultTeamMembers}>
                      {({ field, form }) => (
                        <FnGroupMultiSelect
                          title={CONFIGURATION_TITLES[ConfigurationFields.DefaultTeamMembers]}
                          isRequired={true}
                          options={options[ConfigurationFields.TeamMembers]}
                          value={field.value}
                          placeholder={CONFIGURATION_PLACEHOLDERS[ConfigurationFields.DefaultTeamMembers]}
                          onChange={(selected) => form.setFieldValue(ConfigurationFields.DefaultTeamMembers, selected)}
                        />
                      )}
                    </Field>
                  </DivRow>
                  <DivRow>
                    <Field name={ConfigurationFields.Comments}>
                      {({ field }) => (
                        <FnTextareaField
                          title={CONFIGURATION_TITLES[ConfigurationFields.Comments]}
                          placeholder={CONFIGURATION_PLACEHOLDERS[ConfigurationFields.Comments]}
                          field={field}
                        />
                      )}
                    </Field>
                  </DivRow>
                </DivFormWrapper>
              </FnAntModal>
            </Form>
          )}
        </Formik>
      )}
    </>
  );
};

export default memo(AssignTasksModal);
